import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Camera, MapPin, AtSign, Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { auth } from '../../lib/auth'
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog'

interface Interest {
  id: string
  name: string
  slug: string
}

interface EditProfileModalProps {
  isOpen: boolean
  onClose: () => void
  user: any
  onProfileUpdate?: () => void
  isMandatory?: boolean // For new users who must complete profile
}

export function EditProfileModal({ isOpen, onClose, user, onProfileUpdate, isMandatory = false }: EditProfileModalProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [originalUsername, setOriginalUsername] = useState<string | null>(null)

  // Form fields
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    bio: '',
    location: '',
    email_notifications: true
  })
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)

  // Account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Interests
  const [availableInterests, setAvailableInterests] = useState<Interest[]>([])
  const [selectedInterestIds, setSelectedInterestIds] = useState<Set<string>>(new Set())

  // Load available interests
  useEffect(() => {
    if (isOpen) {
      loadAvailableInterests()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadUserProfile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user])

  const loadAvailableInterests = async () => {
    try {
      const { data, error } = await supabase
        .from('interests')
        .select('*')
        .order('name')

      if (error) throw error
      setAvailableInterests(data || [])
    } catch (err) {
      console.error('Error loading interests:', err)
    }
  }

  const loadUserProfile = async () => {
    try {
      // Get full user profile
      const profile = await auth.getUserProfile(user.id)

      // Store original username to avoid redundant availability checks
      setOriginalUsername(profile?.username || null)

      setFormData({
        first_name: profile?.first_name || user.user_metadata?.first_name || user.user_metadata?.full_name?.split(' ')[0] || '',
        last_name: profile?.last_name || user.user_metadata?.last_name || user.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
        username: profile?.username || '',
        bio: profile?.bio || '',
        location: profile?.location || '',
        email_notifications: profile?.email_notifications ?? true
      })

      // Load user's selected interests
      const { data: userInterests, error } = await supabase
        .from('user_interests')
        .select('interest_id')
        .eq('user_id', user.id)

      if (error) {
        console.error('Error loading user interests:', error)
      } else {
        const interestIds = new Set(userInterests?.map(ui => ui.interest_id) || [])
        setSelectedInterestIds(interestIds)
      }

      // Use Google avatar if available
      setAvatarUrl(
        profile?.avatar_url ||
        user.user_metadata?.avatar_url ||
        user.user_metadata?.picture ||
        ''
      )
    } catch (err) {
      console.error('Error loading profile:', err)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const toggleInterest = (interestId: string) => {
    setSelectedInterestIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(interestId)) {
        newSet.delete(interestId)
      } else {
        newSet.add(interestId)
      }
      return newSet
    })
  }

  // Synchronous format validation (no async/database calls)
  const validateUsernameFormat = (username: string): string | null => {
    if (!username) {
      return 'Username is required'
    }

    // Check format (alphanumeric, underscore, hyphen, 3-30 chars)
    const usernameRegex = /^[a-zA-Z0-9_-]{3,30}$/
    if (!usernameRegex.test(username)) {
      return 'Username must be 3-30 characters and contain only letters, numbers, underscore, or hyphen'
    }

    return null
  }

  // Full validation including availability check (called on blur and save)
  const validateUsername = async (username: string, currentUsername?: string) => {
    // Reset errors
    setUsernameError(null)

    // Check format first
    const formatError = validateUsernameFormat(username)
    if (formatError) {
      setUsernameError(formatError)
      return false
    }

    // Check if username is taken (skip if it's the current user's username)
    if (username !== currentUsername) {
      setCheckingUsername(true)
      try {
        const { data: isAvailable, error } = await supabase
          .rpc('check_username_availability', { proposed_username: username })

        if (error) {
          console.error('Error checking username:', error)
          setUsernameError('Error checking username availability')
          return false
        }

        if (!isAvailable) {
          setUsernameError('Username is already taken')
          return false
        }
      } finally {
        setCheckingUsername(false)
      }
    }

    return true
  }

  const handleUsernameChange = (value: string) => {
    // Remove @ if user types it
    const cleanUsername = value.replace('@', '').toLowerCase()
    handleInputChange('username', cleanUsername)

    // Clear error when user starts typing again
    if (usernameError) {
      setUsernameError(null)
    }
  }

  const handleUsernameBlur = async () => {
    // Only validate on blur if there's a value
    if (formData.username) {
      await validateUsername(formData.username, originalUsername || undefined)
    }
  }


  const convertImageToJpeg = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          // Set max dimensions
          const maxWidth = 800
          const maxHeight = 800
          let width = img.width
          let height = img.height
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width
              width = maxWidth
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height
              height = maxHeight
            }
          }
          
          canvas.width = width
          canvas.height = height
          
          // Draw and convert to JPEG
          ctx?.drawImage(img, 0, 0, width, height)
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const convertedFile = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })
                resolve(convertedFile)
              } else {
                reject(new Error('Failed to convert image'))
              }
            },
            'image/jpeg',
            0.9 // 90% quality
          )
        }
        
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }
      
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsUploadingAvatar(true)
      setError(null)
      
      if (!event.target.files || event.target.files.length === 0) {
        return
      }

      let file = event.target.files[0]
      
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        setError('Image size must be less than 5MB')
        return
      }
      
      // Convert HEIC or any image to JPEG for better compatibility
      const fileType = file.type.toLowerCase()
      const fileName = file.name.toLowerCase()
      
      // Check if it's HEIC/HEIF or needs conversion
      if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || 
          fileType === 'image/heic' || fileType === 'image/heif' ||
          file.size > 1024 * 1024) { // Also convert large files
        console.log('Converting image to JPEG...')
        try {
          file = await convertImageToJpeg(file)
        } catch (conversionError) {
          console.error('Error converting image:', conversionError)
          setError('Failed to process image. Please try a different image or format.')
          return
        }
      }

      const fileExt = 'jpg' // Always use jpg after conversion
      const uploadFileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `avatars/${uploadFileName}`

      console.log('Uploading file:', filePath, 'Size:', file.size)

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(filePath, file, { 
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error details:', uploadError)
        
        // Check if it's a bucket issue
        if (uploadError.message.includes('not found') || uploadError.message.includes('404')) {
          console.log('Bucket not found. Please create the "user-avatars" bucket in Supabase Dashboard')
          setError('Storage not configured. Please contact support or create the storage bucket in Supabase.')
          
          // Try alternative: use the default 'avatars' bucket that might already exist
          console.log('Trying default avatars bucket...')
          const { error: altError } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, { 
              upsert: true,
              contentType: 'image/jpeg'
            })
          
          if (!altError) {
            // Success with alternative bucket
            const { data: { publicUrl } } = supabase.storage
              .from('avatars')
              .getPublicUrl(filePath)
            
            console.log('Upload successful to avatars bucket, public URL:', publicUrl)
            setAvatarUrl(publicUrl)
            setError(null)
            return
          } else {
            console.error('Alternative bucket also failed:', altError)
            
            // Provide instructions for manual bucket creation
            const instructions = `
              To fix this issue, please create a storage bucket in your Supabase dashboard:
              1. Go to your Supabase project dashboard
              2. Navigate to Storage section
              3. Click "New bucket"
              4. Name it "user-avatars" or "avatars"
              5. Set it as Public
              6. Try uploading again
            `
            console.log(instructions)
            throw new Error('Storage bucket not found. Please check console for setup instructions.')
          }
        } else if (uploadError.message.includes('row-level security') || uploadError.message.includes('policy')) {
          throw new Error('Storage permissions error. Please check bucket policies in Supabase.')
        } else {
          throw uploadError
        }
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(filePath)

      console.log('Upload successful, public URL:', publicUrl)
      setAvatarUrl(publicUrl)
      setError(null) // Clear any previous errors
    } catch (error: any) {
      console.error('Error uploading avatar:', error)
      setError(error.message || 'Failed to upload avatar. Please try a different image.')
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)

      // Validate username
      if (!formData.username) {
        setError('Username is required')
        return
      }

      const isUsernameValid = await validateUsername(formData.username, originalUsername || undefined)
      if (!isUsernameValid) {
        return
      }

      // Validate location
      if (!formData.location || formData.location.trim() === '') {
        setError('Location is required')
        return
      }

      // Validate interests (minimum 3)
      if (selectedInterestIds.size < 3) {
        setError('Please select at least 3 interests')
        return
      }

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          avatar_url: avatarUrl
        }
      })

      if (authError) throw authError

      // Update user profile in database
      await auth.updateUserProfile(user.id, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        username: formData.username.toLowerCase(),
        bio: formData.bio,
        location: formData.location,
        avatar_url: avatarUrl,
        email_notifications: formData.email_notifications
      })

      // Update user interests
      // First, delete all existing interests
      await supabase
        .from('user_interests')
        .delete()
        .eq('user_id', user.id)

      // Then insert the selected interests
      if (selectedInterestIds.size > 0) {
        const interestsToInsert = Array.from(selectedInterestIds).map(interestId => ({
          user_id: user.id,
          interest_id: interestId
        }))

        const { error: interestsError } = await supabase
          .from('user_interests')
          .insert(interestsToInsert)

        if (interestsError) {
          console.error('Error saving interests:', interestsError)
          throw new Error('Failed to save interests')
        }
      }

      // Mark profile as completed for new users
      if (isMandatory) {
        const { error: completeError } = await supabase
          .from('users')
          .update({ profile_completed: true })
          .eq('id', user.id)
        
        if (completeError) {
          console.error('Error marking profile as completed:', completeError)
        }
      }

      // Call callback and wait for it to complete
      if (onProfileUpdate) {
        await onProfileUpdate()
      }
      
      // Close modal - the parent component will handle checking profile_completed
      onClose()
    } catch (err: any) {
      console.error('Error updating profile:', err)
      setError(err.message || 'Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)

      const { error } = await supabase.rpc('delete_user_account', { target_user_id: user.id })
      if (error) throw error

      await auth.signOut()
      window.location.href = '/'
    } catch (err: any) {
      console.error('Error deleting account:', err)
      setDeleteError(err.message || 'Failed to delete account. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={isMandatory ? undefined : onClose}
          />

          {/* Modal Container - Centered */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 md:p-8 pointer-events-none overflow-y-auto">
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-3xl bg-[#F2F0E4] shadow-2xl overflow-hidden border border-[#E5E0D6] pointer-events-auto flex flex-col"
              style={{ maxHeight: '90vh' }}
            >
              {/* Decorative Top Border */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-black" />
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-[#E5E0D6]">
                <div>
                  <h2 className="font-serif text-2xl tracking-wide text-[#1A1A1A]">
                    {isMandatory ? 'Complete Your Profile' : 'Edit Profile'}
                  </h2>
                  {isMandatory && (
                    <p className="font-mono text-xs text-gray-500 mt-2 uppercase tracking-widest">Please complete your profile to continue</p>
                  )}
                </div>
                {!isMandatory && (
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-[#EBE8E1] rounded-full transition-colors"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                )}
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-8 py-10 scrollbar-hide">
                {/* Error Alert */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 font-mono text-sm">
                    {error}
                  </div>
                )}

                <div className="flex flex-col space-y-12">
                  {/* Avatar Section */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-sm">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt="Profile"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(`${formData.first_name} ${formData.last_name}`)}&size=128&background=e5e7eb&color=6b7280`
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl text-gray-400 font-semibold bg-gray-100">
                            {formData.first_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-full transition-colors flex items-center justify-center">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                          <Camera size={18} className="text-black" />
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="mt-4 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-black transition-colors"
                    >
                      {isUploadingAvatar ? 'Uploading...' : 'Change Photo'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </div>

                  {/* Form Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                    {/* First Name */}
                    <div className="group">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-black transition-colors">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        placeholder="Enter your first name"
                        className="w-full bg-white border border-[#E5E0D6] px-4 py-3 font-sans text-[#1A1A1A] focus:outline-none focus:border-black focus:ring-0 transition-all placeholder:text-gray-300"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="group">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-black transition-colors">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        placeholder="Enter your last name"
                        className="w-full bg-white border border-[#E5E0D6] px-4 py-3 font-sans text-[#1A1A1A] focus:outline-none focus:border-black focus:ring-0 transition-all placeholder:text-gray-300"
                      />
                    </div>

                    {/* Username */}
                    <div className="group md:col-span-2">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-black transition-colors">
                        Username <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <AtSign size={16} />
                        </div>
                        <input
                          type="text"
                          value={formData.username}
                          onChange={(e) => handleUsernameChange(e.target.value)}
                          onBlur={handleUsernameBlur}
                          placeholder="your_username"
                          className={`w-full bg-white border ${usernameError ? 'border-red-400' : 'border-[#E5E0D6]'} pl-12 pr-4 py-3 font-sans text-[#1A1A1A] focus:outline-none focus:border-black focus:ring-0 transition-all placeholder:text-gray-300`}
                        />
                        {checkingUsername && (
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          </div>
                        )}
                      </div>
                      {usernameError && (
                        <p className="mt-2 font-mono text-[10px] text-red-500">{usernameError}</p>
                      )}
                      <p className="mt-2 font-mono text-[10px] text-gray-400">
                        Your profile will be available at purposecollective.world/profile/{formData.username || 'your_username'}
                      </p>
                    </div>

                    {/* Bio */}
                    <div className="group md:col-span-2">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-black transition-colors">
                        Bio
                      </label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        placeholder="Tell us about yourself..."
                        rows={4}
                        className="w-full bg-white border border-[#E5E0D6] px-4 py-3 font-sans text-[#1A1A1A] focus:outline-none focus:border-black focus:ring-0 transition-all placeholder:text-gray-300 resize-none"
                      />
                    </div>

                    {/* Location */}
                    <div className="group md:col-span-2">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-black transition-colors">
                        Location <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <MapPin size={16} />
                        </div>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          placeholder="City, Country"
                          className="w-full bg-white border border-[#E5E0D6] pl-12 pr-4 py-3 font-sans text-[#1A1A1A] focus:outline-none focus:border-black focus:ring-0 transition-all placeholder:text-gray-300"
                        />
                      </div>
                    </div>

                    {/* Email (Read-only) */}
                    <div className="group md:col-span-2">
                      <label className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={user.email}
                        disabled
                        className="w-full bg-gray-50 border border-[#E5E0D6] px-4 py-3 font-sans text-gray-500 cursor-not-allowed"
                      />
                      <p className="mt-2 font-mono text-[10px] text-gray-400">
                        Email address cannot be changed
                      </p>
                    </div>
                  </div>

                  {/* Interests Section */}
                  <div className="pt-4 border-t border-[#E5E0D6]">
                    <div className="flex items-baseline justify-between mb-6">
                      <label className="block font-mono text-xs uppercase tracking-widest text-black">
                        Interests <span className="text-red-400">*</span>
                      </label>
                      <span className={`font-mono text-[10px] ${selectedInterestIds.size < 3 ? 'text-red-500' : 'text-gray-400'}`}>
                        {selectedInterestIds.size} Selected (Min 3)
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {availableInterests.map(interest => {
                        const isSelected = selectedInterestIds.has(interest.id)
                        return (
                          <button
                            key={interest.id}
                            type="button"
                            onClick={() => toggleInterest(interest.id)}
                            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-all duration-200 border ${
                              isSelected
                                ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                                : 'bg-white text-gray-500 border-[#E5E0D6] hover:border-gray-400 hover:text-black'
                            }`}
                          >
                            {interest.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Notification Settings Section */}
                  <div className="pt-4 border-t border-[#E5E0D6]">
                    <label className="block font-mono text-xs uppercase tracking-widest text-black mb-6">
                      Notification Settings
                    </label>

                    <div className="flex items-center justify-between bg-white border border-[#E5E0D6] px-4 py-4">
                      <div className="flex-1">
                        <p className="font-sans text-[#1A1A1A] text-sm">Receive Curated Weekly Digest</p>
                        <p className="font-mono text-[10px] text-gray-400 mt-1">
                          Receive a weekly edit of handpicked selections from our editors
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleInputChange('email_notifications', !formData.email_notifications)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          formData.email_notifications ? 'bg-[#1A1A1A]' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            formData.email_notifications ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Danger Zone - hidden for mandatory profile completion */}
                  {!isMandatory && (
                    <div className="pt-4 border-t border-[#E5E0D6]">
                      <label className="block font-mono text-xs uppercase tracking-widest text-red-600 mb-2">
                        Danger Zone
                      </label>
                      <p className="font-mono text-[10px] text-gray-400 mb-4">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>

                      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                        <AlertDialogTrigger asChild>
                          <button
                            type="button"
                            className="px-6 py-3 font-mono text-xs uppercase tracking-widest border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                          >
                            Delete Account
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#F2F0E4] border-[#E5E0D6]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif text-xl text-[#1A1A1A]">
                              Delete your account?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="font-sans text-sm text-gray-600">
                              This will permanently delete your account, including all your contributions, saves, comments, and profile data. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          {deleteError && (
                            <div className="p-3 bg-red-50 border border-red-200 text-red-700 font-mono text-xs">
                              {deleteError}
                            </div>
                          )}
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              disabled={isDeleting}
                              className="font-mono text-xs uppercase tracking-widest"
                            >
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => {
                                e.preventDefault()
                                handleDeleteAccount()
                              }}
                              disabled={isDeleting}
                              className="bg-red-600 text-white hover:bg-red-700 font-mono text-xs uppercase tracking-widest"
                            >
                              {isDeleting ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                'Delete Account'
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 border-t border-[#E5E0D6] bg-[#F2F0E4] flex items-center justify-between">
                {!isMandatory ? (
                  <button
                    onClick={onClose}
                    disabled={isSaving}
                    className="font-mono text-xs uppercase tracking-widest text-gray-500 hover:text-black transition-colors px-4 py-2 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                ) : (
                  <div></div>
                )}
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-[#1A1A1A] text-white px-8 py-3 font-mono text-xs font-bold uppercase tracking-widest hover:bg-gray-800 transition-colors flex items-center space-x-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}